import { motion } from 'framer-motion';
import { Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Upgrade Modal — extracted from DesignStudio.jsx (Feb 2026 refactor)
export default function UpgradeModal({ isOpen, onClose, language, isDemo, onLogin }) {
  if (!isOpen) return null;

  const t = {
    tr: {
      title: isDemo ? 'Demo Limitine Ulaştınız!' : 'Tasarım Limitine Ulaştınız!',
      subtitle: isDemo
        ? 'Devam etmek için ücretsiz hesap oluşturun'
        : 'Daha fazla tasarım için planınızı yükseltin',
      demoMessage: '3 ücretsiz demo tasarımınızı kullandınız. Giriş yaparak ayda 3 tasarım daha yapabilirsiniz!',
      freeMessage: 'Bu ay için 3 ücretsiz tasarım hakkınız doldu.',
      loginBtn: 'Giriş Yap / Kayıt Ol',
      upgradeBtn: "Pro'ya Yükselt",
      contactSales: 'Satışla Görüş',
      recommended: 'Önerilen',
      currentPlan: 'Mevcut plan',
      free: 'Ücretsiz',
      pro: 'Pro',
      enterprise: 'Enterprise',
      perMonth: '/ay',
      designs: 'tasarım',
      features: {
        free: ['3 tasarım/ay', 'Temel şablonlar', 'JSON dışa aktarma'],
        pro: ['50 tasarım/ay', 'Tüm şablonlar', 'PDF dışa aktarma', 'İşbirliği'],
        enterprise: ['Özel kullanım limitleri', 'Entegrasyon keşfi', 'Güvenlik incelemesi', 'Öncelikli devreye alma'],
      },
    },
    en: {
      title: isDemo ? 'Demo Limit Reached!' : 'Design Limit Reached!',
      subtitle: isDemo
        ? 'Create a free account to continue'
        : 'Upgrade your plan for more designs',
      demoMessage: "You've used your 3 free demo designs. Sign in to get 3 more designs per month!",
      freeMessage: "You've reached your 3 free designs for this month.",
      loginBtn: 'Login / Sign Up',
      upgradeBtn: 'Upgrade to Pro',
      contactSales: 'Contact Sales',
      recommended: 'Recommended',
      currentPlan: 'Current plan',
      free: 'Free',
      pro: 'Pro',
      enterprise: 'Enterprise',
      perMonth: '/mo',
      designs: 'designs',
      features: {
        free: ['3 designs/month', 'Basic templates', 'JSON export'],
        pro: ['50 designs/month', 'All templates', 'PDF export', 'Collaboration'],
        enterprise: ['Custom usage limits', 'Integration discovery', 'Security review', 'Priority onboarding'],
      },
    },
  }[language] || {};

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-amber-500/30 rounded-2xl w-full max-w-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 bg-gradient-to-r from-amber-950/50 to-orange-950/50 text-center">
          <Crown className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <h3 className="text-2xl font-bold text-white">{t.title}</h3>
          <p className="text-amber-200 mt-2">{t.subtitle}</p>
        </div>

        <div className="p-6">
          <p className="text-slate-300 text-center mb-6">
            {isDemo ? t.demoMessage : t.freeMessage}
          </p>

          {isDemo ? (
            <div className="text-center">
              <Button
                onClick={onLogin}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-lg px-8 py-6"
              >
                {t.loginBtn}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 opacity-50">
                <h4 className="font-bold text-white text-center">{t.free}</h4>
                <p className="text-2xl font-bold text-center text-slate-400 my-2">$0</p>
                <ul className="text-xs text-slate-400 space-y-1">
                  {t.features?.free?.map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
                <p className="text-center text-xs text-slate-500 mt-3">({t.currentPlan})</p>
              </div>

              <div className="bg-gradient-to-b from-green-950/50 to-emerald-950/50 border-2 border-green-500/50 rounded-xl p-4 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white text-xs rounded-full">
                  {t.recommended}
                </div>
                <h4 className="font-bold text-white text-center">{t.pro}</h4>
                <p className="text-2xl font-bold text-center text-green-400 my-2">$19.99<span className="text-sm">{t.perMonth}</span></p>
                <ul className="text-xs text-slate-300 space-y-1">
                  {t.features?.pro?.map((f, i) => <li key={i}>✓ {f}</li>)}
                </ul>
                <Button
                  onClick={() => { window.location.href = '/dashboard?tab=subscription'; }}
                  className="w-full mt-3 bg-green-600 hover:bg-green-500 text-sm"
                >
                  {t.upgradeBtn}
                </Button>
              </div>

              <div className="bg-slate-800/50 border border-purple-500/30 rounded-xl p-4">
                <h4 className="font-bold text-white text-center">{t.enterprise}</h4>
                <p className="text-2xl font-bold text-center text-purple-400 my-2">Custom</p>
                <ul className="text-xs text-slate-300 space-y-1">
                  {t.features?.enterprise?.map((f, i) => <li key={i}>✓ {f}</li>)}
                </ul>
                <Button
                  onClick={() => { window.location.href = '/contact'; }}
                  variant="outline"
                  className="w-full mt-3 border-purple-500/50 text-purple-400 text-sm"
                >
                  {t.contactSales}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-800 text-center">
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-sm">
            {language === 'tr' ? 'Daha sonra' : 'Maybe later'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
